import React, { useEffect, useState, useRef, useContext } from "react";
import { FileUp, Trash2 } from "lucide-react";
import { IntlContext } from "context";
import { fetchFile } from "hooks/file";
import { ACCEPTED_IMAGE_MIMES, UPLOAD_MAX_MEGABYTES, isFileTooLarge } from "helpers/file/uploadConstraints";
import { Button } from "components/ui/button";
import { cn } from "lib/utils";

export interface BlobFile {
    id?: string;
    name: string;
    size: number;
    content: Blob;
    type: string;
}

export interface InputProps
    extends Omit<
        Omit<React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>, "onChange">,
        "value"
    > {
    name: string;
    type?: "file";
    onChange?: (value: BlobFile | null) => void;
    value?: BlobFile;
}

const InputFile: React.FC<InputProps & { changeLabel?: string; compact?: boolean }> = ({
    autoComplete = "off",
    type = "file",
    onChange,
    value,
    accept = ACCEPTED_IMAGE_MIMES,
    multiple,
    disabled,
    changeLabel,
    compact = false,
    ...props
}) => {
    const intl = useContext(IntlContext);
    const [currentValue, setCurrentValue] = useState<BlobFile | undefined>(value);
    const [currentFile, setCurrentFile] = useState<(BlobFile & { preview: string }) | undefined>(undefined);
    const [sizeError, setSizeError] = useState(false);
    const inputEl = useRef<HTMLInputElement>(null);

    const onChangeHanlder = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
        let blobFile = null;
        if (target.files) {
            const file = target.files.item(0);
            if (file) {
                if (isFileTooLarge(file)) {
                    setSizeError(true);
                    target.value = "";
                    setCurrentValue(undefined);
                    onChange && onChange(null);
                    return;
                }
                blobFile = Object.assign({ name: file.name, size: file.size, type: file.type }, { content: file });
            }
        }
        setSizeError(false);
        setCurrentValue(blobFile ?? undefined);
        onChange && onChange(blobFile);
    };

    const clearFile = () => {
        if (inputEl.current) {
            inputEl.current.value = "";
        }
        onChange && onChange(null);
        setCurrentFile(undefined);
        setCurrentValue(undefined);
        setSizeError(false);
    };

    useEffect(() => {
        if (!currentValue) {
            setCurrentFile(undefined);
            return;
        }

        if (!currentValue.content && currentValue.id) {
            fetchFile(currentValue.id).then((document) => {
                const newValue = Object.assign({}, currentValue, document);
                setCurrentValue(newValue);
                onChange && onChange(newValue);
            });
        }

        if (currentValue.content) {
            const preview = URL.createObjectURL(currentValue.content);
            const newFile = Object.assign({ ...currentValue }, { preview });
            setCurrentFile(newFile);

            return () => {
                URL.revokeObjectURL(newFile.preview);
            };
        }
    }, [currentValue, onChange]);

    useEffect(() => {
        setCurrentValue(value);
    }, [value]);

    const selectLabel = changeLabel ?? intl.formatMessage("generic.action.upload.select");

    return (
        <div className={cn("field-control-file", currentFile && "has-file")}>
            <input
                className="sr-only"
                autoComplete={autoComplete}
                type={type}
                onChange={onChangeHanlder}
                ref={inputEl}
                accept={accept}
                multiple={multiple}
                disabled={disabled}
                {...props}
            />

            {!disabled && (
                <div
                    className={cn(
                        "rounded-[10px] border border-dashed border-gray-200 bg-muted/30",
                        compact
                            ? "flex h-11 items-center gap-2 px-2"
                            : "flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between",
                        sizeError && "border-destructive/50",
                    )}
                >
                    {compact ? (
                        <>
                            <FileUp className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                            <div className="min-w-0 flex-1">
                                {currentFile && (
                                    <a
                                        href={currentFile.preview}
                                        download={currentFile.name}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block truncate text-xs font-medium text-primary hover:underline"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {currentFile.name}
                                    </a>
                                )}
                            </div>
                            {currentFile && (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 shrink-0 rounded-lg text-muted-foreground hover:text-destructive"
                                    onClick={clearFile}
                                    aria-label={intl.formatMessage("generic.action.upload.remove")}
                                >
                                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                                </Button>
                            )}
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-8 shrink-0 rounded-lg px-2 text-xs"
                                onClick={() => inputEl.current?.click()}
                            >
                                {selectLabel}
                            </Button>
                        </>
                    ) : (
                        <>
                            <div className="flex min-w-0 items-center gap-3">
                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-border bg-background">
                                    <FileUp className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                                </div>
                                <div className="min-w-0">
                                    {currentFile && (
                                        <a
                                            href={currentFile.preview}
                                            download={currentFile.name}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="block truncate text-sm font-medium text-primary hover:underline"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {currentFile.name}
                                        </a>
                                    )}
                                </div>
                            </div>

                            <div className="flex shrink-0 items-center gap-2 self-start sm:self-auto">
                                {currentFile && (
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="h-10 w-10 rounded-xl text-muted-foreground hover:text-destructive"
                                        onClick={clearFile}
                                        aria-label={intl.formatMessage("generic.action.upload.remove")}
                                    >
                                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                                    </Button>
                                )}
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="rounded-xl"
                                    onClick={() => inputEl.current?.click()}
                                >
                                    {selectLabel}
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            )}

            {sizeError && (
                <span className="field__feedback error mt-2 block">
                    {intl.formatMessage("generic.upload.too_large", { max: UPLOAD_MAX_MEGABYTES })}
                </span>
            )}
        </div>
    );
};

export default InputFile;
